import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { cleanTokens, getAccessToken, getRefreshToken, refreshAccessToken } from 'services/auth';
import { getIsDevModeFlag } from 'services/dev-mode/dev-mode.ts';

interface CustomHttpError {
  key: string;
  message: string;
  fields: string;
  lang: string;
  timestamp: string;
  path: string;
}

export type { AxiosResponse, AxiosError } from 'axios';
export type AxiosCustomError = AxiosError<CustomHttpError>;

const defaultOptions: AxiosRequestConfig = {
  withCredentials: true,
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    Accept: 'application/json',
  },
};

const http: AxiosInstance = axios.create(defaultOptions);

http.interceptors.request.use(async (requestConfig) => {
  if (requestConfig.withCredentials) {
    let accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    const needRefresh = !accessToken && Boolean(refreshToken);

    if (needRefresh) {
      accessToken = await refreshAccessToken(http);
    }

    if (accessToken) {
      requestConfig.headers['Authorization'] = `Bearer ${String(accessToken)}`;
    } else {
      // todo логирование
      // captureMessage('trying to access a private url without token', {
      //   level: 'error',
      //   method: requestConfig.method,
      //   baseURL: requestConfig.baseURL,
      //   url: requestConfig.url,
      // });
      throw new axios.Cancel('Operation canceled');
    }
  }

  const isDevMode = getIsDevModeFlag();

  if (isDevMode) {
    requestConfig.headers['X-Dev-Mode'] = isDevMode;
  }

  return requestConfig;
});

http.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      try {
        const refreshToken = getRefreshToken();

        if (refreshToken) {
          const newAccessToken = await refreshAccessToken(http);
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          error.config.headers['Authorization'] = `Bearer ${String(newAccessToken)}`;
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return http(error.config);
        } else {
          cleanTokens();
          window.location.href = '/auth';
        }
      } catch (refreshError) {
        console.log(refreshError);
        cleanTokens();
        window.location.href = '/auth';
        throw refreshError;
      }
    }

    return Promise.reject(error);
  },
);

export { http };
